(function (Drupal, once) {
  'use strict';

  const FORM_SELECTOR = '#views-exposed-form-catalog-page-standard';
  const LAST_CLICKED_KEY = 'catalog_last_clicked_filter';
  const MISC_KEYS = ['license', 'badge', 'textlanguage'];
  const TOPIC_SERIES_KEYS = ['topic', 'series'];
  const ALL_KEYS = [...TOPIC_SERIES_KEYS, ...MISC_KEYS];
  const KEY_ALIASES = {
    field_topic_target_id: 'topic',
    field_series_target_id: 'series',
    field_license_target_id: 'license',
    field_label_target_id: 'badge',
    field_textlanguage_target_id: 'textlanguage',
  };
  const CONTAINER_CLASS_TO_KEY = {
    'form-item-topic': 'topic',
    'form-item-series': 'series',
    'form-item-license': 'license',
    'form-item-badge': 'badge',
    'form-item-textlanguage': 'textlanguage',
  };
  let isBound = false;
  let isScrollTopDisabled = false;

  function disableViewsAjaxScrollTop() {
    if (isScrollTopDisabled) {
      return;
    }

    if (!Drupal.AjaxCommands || !Drupal.AjaxCommands.prototype) {
      return;
    }

    // Views AJAX adds a scrollTop command by default; disable it on this page.
    Drupal.AjaxCommands.prototype.scrollTop = function () {};
    isScrollTopDisabled = true;
  }

  function hasAnyQueryParameters() {
    const params = new URLSearchParams(window.location.search);
    return Array.from(params.keys()).length > 0;
  }

  function setFormValue(formEl, key, value) {
    let control = formEl.querySelector(`[name="${key}"]`);

    if (!control) {
      control = document.createElement('input');
      control.type = 'hidden';
      control.name = key;
      formEl.appendChild(control);
    }

    control.value = value;
  }

  function getClickedValue(link, key) {
    const url = new URL(link.href, window.location.href);
    const value = url.searchParams.get(key);
    return (value === null || value === '') ? 'All' : value;
  }

  function getCurrentFilterValue(formEl, key) {
    const control = formEl.querySelector(`[name="${key}"]`);
    if (!control) {
      return 'All';
    }

    const value = control.value;
    return (value === null || value === '') ? 'All' : value;
  }

  function findFilterContainer(formEl, key) {
    return formEl.querySelector(`.bef-links[data-name="${key}"]`);
  }

  function setSelectedClassForFilter(formEl, key, value) {
    const container = findFilterContainer(formEl, key);
    if (!container) {
      return;
    }

    const links = container.querySelectorAll('a.bef-link');
    links.forEach((linkEl) => linkEl.classList.remove('bef-link--selected'));

    const targetName = `${key}[${value}]`;
    const targetByName = Array.from(links).find(
      (linkEl) => linkEl.getAttribute('name') === targetName
    );

    if (targetByName) {
      targetByName.classList.add('bef-link--selected');
      return;
    }

    // Fallback for malformed/missing name attributes.
    const targetByUrl = Array.from(links).find((linkEl) => {
      const url = new URL(linkEl.href, window.location.href);
      const currentValue = url.searchParams.get(key) ?? 'All';
      return currentValue === value;
    });

    if (targetByUrl) {
      targetByUrl.classList.add('bef-link--selected');
    }
  }

  function submitExposedForm(formEl) {
    const submitButton = formEl.querySelector(
      'input[type="submit"], button[type="submit"], .form-submit'
    );

    if (submitButton) {
      submitButton.click();
      return;
    }

    if (typeof formEl.requestSubmit === 'function') {
      formEl.requestSubmit();
      return;
    }

    // Fallback: keep submit handlers interceptable.
    const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
    if (formEl.dispatchEvent(submitEvent)) {
      HTMLFormElement.prototype.submit.call(formEl);
    }
  }

  function applyMutualExclusion(formEl, activeKey, keys, clickedValue) {
    keys.forEach((key) => {
      const value = (key === activeKey) ? clickedValue : 'All';
      setFormValue(formEl, key, value);
      setSelectedClassForFilter(formEl, key, value);
    });
    setFormValue(formEl, LAST_CLICKED_KEY, activeKey);
  }

  function getMutualExclusiveGroup(activeKey) {
    if (TOPIC_SERIES_KEYS.includes(activeKey)) {
      return TOPIC_SERIES_KEYS;
    }

    if (MISC_KEYS.includes(activeKey)) {
      return MISC_KEYS;
    }

    return null;
  }

  function normalizeKey(rawKey) {
    if (!rawKey) {
      return '';
    }

    return KEY_ALIASES[rawKey] || rawKey;
  }

  function resolveKeyFromLinkName(link) {
    const rawName = link.getAttribute('name') || '';
    if (!rawName) {
      return '';
    }

    const base = rawName.split('[')[0];
    const normalized = normalizeKey(base);
    return ALL_KEYS.includes(normalized) ? normalized : '';
  }

  function resolveKeyFromContainerClass(link) {
    const classNames = Object.keys(CONTAINER_CLASS_TO_KEY);
    for (const className of classNames) {
      const container = link.closest(`.${className}`);
      if (!container) {
        continue;
      }

      const key = CONTAINER_CLASS_TO_KEY[className];
      if (ALL_KEYS.includes(key)) {
        return key;
      }
    }

    return '';
  }

  function resolveActiveKey(formEl, link) {
    const container = link.closest('.bef-links[data-name]');
    const fromContainer = normalizeKey(container?.getAttribute('data-name'));
    if (ALL_KEYS.includes(fromContainer)) {
      return fromContainer;
    }

    const fromLinkName = resolveKeyFromLinkName(link);
    if (fromLinkName) {
      return fromLinkName;
    }

    const fromContainerClass = resolveKeyFromContainerClass(link);
    if (fromContainerClass) {
      return fromContainerClass;
    }

    const linkUrl = new URL(link.href, window.location.href);
    const keysInUrl = ALL_KEYS.filter((key) => linkUrl.searchParams.has(key));
    if (keysInUrl.length === 1) {
      return keysInUrl[0];
    }

    // If multiple keys are present, prefer one explicitly set to non-"All".
    const explicitNonAll = keysInUrl.filter((key) => {
      const clickedValue = linkUrl.searchParams.get(key);
      return clickedValue !== null && clickedValue !== '' && clickedValue !== 'All';
    });
    if (explicitNonAll.length === 1) {
      return explicitNonAll[0];
    }

    return '';
  }

  function onFilterLinkClick(event) {
    const link = event.target.closest('a.bef-link');
    if (!link) {
      return;
    }

    const formEl = link.closest(FORM_SELECTOR);
    if (!formEl) {
      return;
    }

    const activeKey = resolveActiveKey(formEl, link);
    if (!activeKey) {
      return;
    }

    const group = getMutualExclusiveGroup(activeKey);
    if (!group) {
      return;
    }

    // On the initial clean URL (no query params), do not hijack.
    // This avoids adding delay on first interaction.
    if (!hasAnyQueryParameters()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    const currentValue = getCurrentFilterValue(formEl, activeKey);
    let clickedValue = getClickedValue(link, activeKey);

    // Toggle behavior: same value clicked again => reset to All.
    if (clickedValue !== 'All' && clickedValue === currentValue) {
      clickedValue = 'All';
    }

    applyMutualExclusion(formEl, activeKey, group, clickedValue);
    submitExposedForm(formEl);
  }

  function bindGlobalClickHandler() {
    if (isBound) {
      return;
    }

    // Capture phase lets us intercept before BEF's own link click handlers.
    document.addEventListener('click', onFilterLinkClick, true);
    isBound = true;
  }

  Drupal.behaviors.catalogFilterMutualExclusive = {
    attach(context) {
      once('catalog-filter-mutual-exclusive', FORM_SELECTOR, context);
      disableViewsAjaxScrollTop();
      bindGlobalClickHandler();
    },
  };

  // Bind as soon as this file executes; behavior attach remains as safety.
  disableViewsAjaxScrollTop();
  bindGlobalClickHandler();
})(Drupal, once);
